/**
 * @module  Experience
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { catchError, map } from 'rxjs/operators';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { APIError } from '@support/api/common/error.interface';
import {
  GenerateOneTimePinResponse,
  ValidateOneTimePinResponse,
} from '../interfaces/one-time-pin.interface';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { OTP_CONSTANTS } from '../constants/one-time-pin.constant';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import {
  OneTimePinContentData,
  OtpModalAEMInterface,
} from '@core/aem/interfaces/one-time-pin/one-time-pin.interface';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import {
  PermissionOverridePayload,
  PermissionOverrideResponse,
} from '@support/shared/widgets/interfaces/permission-override-modal.interface';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { PostCustomerVerificationResponse } from '@support/api/proxies/responses/api-2016.interface';
import { BodyParams } from '@support/api/proxies/requests/api-2016.interface';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { OverrideOTPActivityLogResponse } from '@support/api/proxies/responses/api_2832.interface';

@Injectable()
export class OneTimePinValidationService {
  constructor(
    private stateStoreUtils: StateStoreUtilsService,
    private apiGatewayService: ApiGatewayService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private contentProviderService: ContentProviderService,
    private commonApiService: CommonApiService
  ) {}

  public validateIfOTPNeeded(): boolean {
    const isOTPVerificationEnabled = this.featureToggleUtilService.isOTPVerificationEnabled();

    /*
      After cutoff date, we do not allow Bypass as a valid verification
     */
    let isPreverified;
    if (isOTPVerificationEnabled) {
      if (this.stateStoreUtils.isAccountActivationAfterOTPCutoff()) {
        isPreverified =
          this.stateStoreUtils.getCustomerOneTimePinValidationStatus() &&
          !(OTP_CONSTANTS.BYPASS === this.stateStoreUtils.getCustomerVerificationType());
      } else {
        isPreverified = this.stateStoreUtils.getCustomerOneTimePinValidationStatus();
      }
    }

    return !!isOTPVerificationEnabled && !isPreverified;
  }

  public generateOneTimePin(
    isSMS: boolean,
    otpSentTo: string
  ): Observable<GenerateOneTimePinResponse> {
    const bscsCustomerID = this.stateStoreUtils.getBscsCustomerID();
    let lineDetailsRequest: object;
    const userId = this.stateStoreUtils.getProfileId();
    const headers = new HttpHeaders();
    if (!!isSMS) {
      lineDetailsRequest = {
        bscsCustomerId: bscsCustomerID,
        userId: userId,
        msisdn: otpSentTo,
      };
    } else {
      lineDetailsRequest = {
        bscsCustomerId: bscsCustomerID,
        userId: userId,
        email: otpSentTo,
      };
    }

    return this.apiGatewayService
      .postRequest(
        ASSISTED_API_MAP.API_2213_postGenerateTempPin,
        lineDetailsRequest,
        headers,
        null,
        null
      )
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  public validateOneTimePin(
    pin: string,
    isSMS: boolean,
    otpSentTo: string
  ): Observable<ValidateOneTimePinResponse> {
    const bscsCustomerID = this.stateStoreUtils.getBscsCustomerID();
    const userId = this.stateStoreUtils.getProfileId();
    const headers = new HttpHeaders();
    let payload: object;

    if (!!isSMS) {
      payload = {
        bscsCustomerId: bscsCustomerID,
        msisdn: otpSentTo,
        userId: userId,
        tempPin: pin,
      };
    } else {
      payload = {
        bscsCustomerId: bscsCustomerID,
        email: otpSentTo,
        userId: userId,
        tempPin: pin,
      };
    }

    return this.apiGatewayService
      .postRequest(ASSISTED_API_MAP.API_2212_postValidateTempPin, payload, headers, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Calls content-provider-service to fetch AEM data.
   * @returns Observable<OneTimePinContentData>
   */
  public getOtpAemContent(): Observable<OneTimePinContentData> {
    return this.contentProviderService.getAEMRequest(CONTENT_ENDPOINT.ONE_TIME_PIN_MODAL).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      map((data: OtpModalAEMInterface) => {
        return data.model['model-data']['module-enhancements'][0];
      })
    );
  }

  /**
   * Call Manager Override API_2218 and return Response
   * @param permissionOverridePayload
   * @private
   */
  public managerOverride(
    permissionOverridePayload: PermissionOverridePayload
  ): Observable<PermissionOverrideResponse> {
    return this.apiGatewayService
      .postRequest(
        ASSISTED_API_MAP.API_2218_postManagerOverrideRequest,
        permissionOverridePayload,
        null,
        null
      )
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError) => observableThrowError(error))
      );
  }

  public postVerification(): Observable<PostCustomerVerificationResponse> {
    const headers = new HttpHeaders();
    const customerInfo = this.stateStoreUtils.getAccountLinesInfo();
    const repInfo = this.stateStoreUtils.getRepProfile();

    const verificationPayload: BodyParams = {
      accountId: this.stateStoreUtils.getAccountId(),
      customerId: this.stateStoreUtils.getBscsCustomerID(),
      customerFullName: `${customerInfo.customer.firstName} ${customerInfo.customer.lastName}`,
      customerRole: 'ACCOUNTOWNER',
      dealerCode: this.stateStoreUtils.getDealerCode(),
      dealerFullName: '',
      msisdn: this.stateStoreUtils.getAuthPrimaryMsisdn(),
      repFullName: `${repInfo.repFirstName} ${repInfo.repLastName}`,
      repNTId: repInfo.repNTId,
      repStoreId: repInfo.repStoreId,
      startTime: new Date().toLocaleDateString(),
      verificationIndicator: 'true',
    };

    return this.apiGatewayService
      .postRequest(
        API_MAP.API_2016_postCustomerVerification,
        verificationPayload,
        headers,
        null,
        null
      )
      .pipe(
        map((verficationResponse: HttpResponse<Object>) => {
          return verficationResponse.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Handles adding Manager Override to Activity Log
   * @returns void
   */
  public logManagerOverrideActivity(): void {
    if (!this.featureToggleUtilService.isOTPVerificationEnabled()) {
      return;
    }
    const isBypass = this.stateStoreUtils.getCustomerVerificationType() === OTP_CONSTANTS.BYPASS;
    const managerOverridePerformed = this.stateStoreUtils.getManagerOverridePerformedStatus();
    if (isBypass && !managerOverridePerformed) {
      this.stateStoreUtils.setManagerOverridePerformedStatus(true);
      this.commonApiService.postActivityLog().subscribe((data: OverrideOTPActivityLogResponse) => {
        // Response not considered
      });
    }
  }

  /**
   * If verification type is Bypass and Manager Override is not allowed, then prevent Action
   */
  public isManagerOverrideActionAllowed(): boolean {
    const verificationType = this.stateStoreUtils.getCustomerVerificationType();
    if (
      OTP_CONSTANTS.BYPASS === verificationType &&
      !this.stateStoreUtils.isManagerOverrideAllowed()
    ) {
      return false;
    } else {
      return true;
    }
  }
}
